import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  FileText,
  Home,
  BookOpen,
  Code,
  Upload,
  History,
  Activity,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./SIPLayout.css";

function SIPLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Get API Docs URL for SIP pipeline
  const getSIPApiDocsUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001/api/extraction/sip-pipeline/docs';
    }
    return `${window.location.protocol}//${window.location.hostname}/api/extraction/sip-pipeline/docs`;
  };

  const menuItems = [
    { path: "/sip", icon: Home, label: "Dashboard", exact: true },
    { path: "/sip/dashboard", icon: Home, label: "Dashboard" },
    { path: "/sip/extract", icon: Upload, label: "Process OCR", color: "#3b82f6" },
    { path: "/sip/history", icon: History, label: "Work Orders", color: "#8b5cf6" },
    { path: "/sip/api-logs", icon: Code, label: "API Logs" },
    { path: getSIPApiDocsUrl(), icon: BookOpen, label: "API Docs", external: true },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    if (item.path === "/sip/dashboard" && (location.pathname === "/sip" || location.pathname === "/sip/")) {
      return true;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/sip" || location.pathname === "/sip/" || location.pathname === "/sip/dashboard") {
      return "SIP Pipeline Dashboard";
    }
    if (location.pathname === "/sip/extract") {
      return "Process SIP OCR";
    }
    if (location.pathname === "/sip/history") {
      return "Work Orders";
    }
    if (location.pathname.startsWith("/sip/work-order/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.label;
    }
    return "SIP OCR Pipeline";
  };

  return (
    <div className="sip-layout">
      {/* Sidebar */}
      <aside className="sip-sidebar">
        <div className="sip-sidebar-header">
          {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
            <button 
              onClick={() => navigate("/dashboard")} 
              className="sip-back-btn"
              title="Back to Main Dashboard"
            >
              <span>←</span> Main Dashboard
            </button>
          )}
          <div className="sip-logo">
            <Activity size={24} className="sip-logo-icon" />
            <span className="sip-logo-text">SIP Pipeline</span>
          </div>
          <div className="sip-logo-subtitle">SIP Form OCR Processing</div>
        </div>

        <nav className="sip-sidebar-nav">
          {menuItems.filter(item => !item.exact).map((item) => (
            <button
              key={item.path}
              className={`sip-nav-item ${isActive(item) ? "active" : ""} ${item.external ? "sip-nav-item-external" : ""}`}
              onClick={() => {
                if (item.external) {
                  window.open(item.path, "_blank");
                } else {
                  navigate(item.path);
                }
              }}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="sip-nav-item-content">
                <span className="sip-nav-label">{item.label}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="sip-sidebar-footer">
          <button
            className="sip-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="sip-nav-item-content">
              <span className="sip-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="sip-layout-content">
        {/* Top Header */}
        <header className="sip-top-header">
          <div className="sip-header-title">{getPageTitle()}</div>

          <div className="sip-header-actions">
            <NotificationsPanel />
            
            <div className="sip-user-menu">
              <button
                className="sip-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="sip-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="sip-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="sip-user-dropdown">
                  <div className="sip-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="sip-dropdown-name">{user?.name || 'SIP User'}</div>
                      <div className="sip-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="sip-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="sip-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default SIPLayout;
